import React, { useState, useEffect } from "react";
import AdminLayout from "../../components/AdminLayout";
import Modal from "../../components/Modal";
import api, { baseURL } from "../../services/api";
import { toast } from "react-toastify";
import {
  FiInbox,
  FiMail,
  FiClock,
  FiUser,
  FiCheckCircle,
  FiEye,
  FiTrash2,
  FiSend,
  FiSearch,
  FiMessageCircle,
  FiPaperclip,
  FiFile,
  FiImage,
} from "react-icons/fi";

const MemberMessages = () => {
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedComm, setSelectedComm] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Search & filter
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    fetchCommunications();
    fetchUnreadCount();
  }, []);

  const fetchCommunications = async () => {
    try {
      const response = await api.get("/member-communications/admin");
      setCommunications(response.data);
    } catch (error) {
      console.error("Error fetching communications:", error);
      toast.error("Failed to load member messages");
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await api.get(
        "/member-communications/admin/unread-count"
      );
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  };

  const handleView = async (comm) => {
    try {
      const response = await api.get(`/member-communications/admin/${comm.id}`);
      setSelectedComm(response.data);
      setShowViewModal(true);
      fetchCommunications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error fetching communication:", error);
      toast.error("Failed to load message details");
    }
  };

  const handleReply = (comm) => {
    setSelectedComm(comm);
    setReplyText(comm.admin_reply || "");
    setShowReplyModal(true);
  };

  const submitReply = async () => {
    if (!replyText.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/member-communications/admin/${selectedComm.id}/reply`, {
        reply: replyText,
      });
      toast.success("Reply sent successfully!");
      setShowReplyModal(false);
      setReplyText("");
      setSelectedComm(null);
      fetchCommunications();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this message?")) {
      return;
    }

    try {
      await api.delete(`/member-communications/admin/${id}`);
      toast.success("Message deleted successfully");
      fetchCommunications();
      fetchUnreadCount();
    } catch (error) {
      console.error("Error deleting communication:", error);
      toast.error("Failed to delete message");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "unread":
        return <span className="badge badge-warning">Unread</span>;
      case "read":
        return <span className="badge badge-info">Read</span>;
      case "replied":
        return <span className="badge badge-success">Replied</span>;
      default:
        return <span className="badge">{status}</span>;
    }
  };

  const filteredCommunications = communications.filter((comm) => {
    const matchesSearch =
      comm.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.member_name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus =
      statusFilter === "all" || comm.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <AdminLayout>
        <div className="spinner"></div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="page-header">
        <h1 className="page-title">
          {/* <FiInbox style={{ marginRight: "0.5rem" }} /> */}
          Member Messages
          {unreadCount > 0 && (
            <span
              style={{
                backgroundColor: "#dc3545",
                color: "white",
                borderRadius: "50%",
                padding: "0.25rem 0.5rem",
                fontSize: "0.8rem",
                marginLeft: "0.5rem",
              }}
            >
              {unreadCount}
            </span>
          )}
        </h1>
      </div>

      {/* Search & Filter */}
      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              flex: 1,
              minWidth: "200px",
            }}
          >
            <FiSearch style={{ color: "#666" }} />
            <input
              type="text"
              placeholder="Search by subject, message, or member..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ border: "none", outline: "none", flex: 1 }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            <option value="all">All Status</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
            <option value="replied">Replied</option>
          </select>
        </div>
      </div>

      {/* Messages Table */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">
            {/* <FiMessageCircle style={{ marginRight: "0.5rem" }} /> */}
            Messages ({filteredCommunications.length})
          </h2>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCommunications.map((comm) => (
                <tr
                  key={comm.id}
                  style={{
                    backgroundColor:
                      comm.status === "unread" ? "#fff9e6" : "transparent",
                  }}
                >
                  <td>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiUser />
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {comm.member_name}
                        </div>
                        <small style={{ color: "#666" }}>
                          {comm.member_email}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div
                      style={{
                        fontWeight: comm.status === "unread" ? 600 : 400,
                      }}
                    >
                      {comm.subject}
                    </div>
                    <small
                      style={{
                        color: "#666",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "block",
                        maxWidth: "200px",
                      }}
                    >
                      {comm.message}
                    </small>
                  </td>
                  <td>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                      }}
                    >
                      <FiClock size={14} />
                      {formatDate(comm.created_at)}
                    </span>
                  </td>
                  <td>{getStatusBadge(comm.status)}</td>
                  <td>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleView(comm)}
                        style={{ padding: "0.4rem 0.6rem" }}
                        title="View"
                      >
                        <FiEye size={16} />
                      </button>
                      <button
                        className="btn btn-primary"
                        onClick={() => handleReply(comm)}
                        style={{ padding: "0.4rem 0.6rem" }}
                        title="Reply"
                      >
                        <FiSend size={16} />
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => handleDelete(comm.id)}
                        style={{ padding: "0.4rem 0.6rem" }}
                        title="Delete"
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredCommunications.length === 0 && (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    {/* <FiMail
                      size={32}
                      style={{ marginBottom: "0.5rem", opacity: 0.5 }}
                    /> */}
                    <p style={{ color: "#666" }}>No messages found</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedComm(null);
        }}
        title="Message Details"
      >
        {selectedComm && (
          <div>
            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>From</label>
              <p style={{ margin: "0.25rem 0" }}>
                {selectedComm.member_name} ({selectedComm.member_email})
              </p>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Subject</label>
              <p style={{ margin: "0.25rem 0" }}>{selectedComm.subject}</p>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Status</label>
              <div style={{ marginTop: "0.25rem" }}>
                {getStatusBadge(selectedComm.status)}
              </div>
            </div>

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>Message</label>
              <p
                style={{
                  margin: "0.25rem 0",
                  whiteSpace: "pre-wrap",
                  backgroundColor: "#f5f5f5",
                  padding: "1rem",
                  borderRadius: "8px",
                }}
              >
                {selectedComm.message}
              </p>
            </div>

            {selectedComm.voice_file_path && (
              <div style={{ marginBottom: "1rem" }}>
                <label style={{ fontWeight: 600, color: "#666" }}>
                  Voice Message
                </label>
                <audio controls style={{ width: "100%", marginTop: "0.5rem" }}>
                  <source src={`${baseURL}${selectedComm.voice_file_path}`} />
                  Your browser does not support the audio element.
                </audio>
              </div>
            )}

            {selectedComm.document_file_path && (
              <div style={{ marginBottom: "1rem" }}>
                <label
                  style={{
                    fontWeight: 600,
                    color: "#666",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FiPaperclip />
                  Attached Document
                </label>
                <div style={{ marginTop: "0.5rem" }}>
                  {selectedComm.document_file_path.match(
                    /\.(jpg|jpeg|png|gif|webp)$/i
                  ) ? (
                    <div>
                      <img
                        src={`${baseURL}${selectedComm.document_file_path}`}
                        alt="Attached document"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "300px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <div style={{ marginTop: "0.5rem" }}>
                        <a
                          href={`${baseURL}${selectedComm.document_file_path}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                          style={{
                            fontSize: "0.85rem",
                            padding: "0.4rem 0.8rem",
                          }}
                        >
                          <FiImage style={{ marginRight: "0.25rem" }} />
                          View Full Size
                        </a>
                      </div>
                    </div>
                  ) : (
                    <a
                      href={`${baseURL}${selectedComm.document_file_path}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}
                    >
                      <FiFile />
                      View/Download PDF
                    </a>
                  )}
                </div>
              </div>
            )}

            <div style={{ marginBottom: "1rem" }}>
              <label style={{ fontWeight: 600, color: "#666" }}>
                Received On
              </label>
              <p style={{ margin: "0.25rem 0" }}>
                {formatDate(selectedComm.created_at)}
              </p>
            </div>

            {selectedComm.status === "replied" && selectedComm.admin_reply && (
              <div
                style={{
                  marginTop: "1.5rem",
                  padding: "1rem",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                  borderLeft: "4px solid #28a745",
                }}
              >
                <label
                  style={{
                    fontWeight: 600,
                    color: "#28a745",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  <FiCheckCircle />
                  Your Reply
                </label>
                <p style={{ margin: "0.5rem 0", whiteSpace: "pre-wrap" }}>
                  {selectedComm.admin_reply}
                </p>
                <small style={{ color: "#666" }}>
                  Replied on: {formatDate(selectedComm.replied_at)}
                </small>
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                marginTop: "1.5rem",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedComm(null);
                }}
              >
                Close
              </button>
              {selectedComm.status !== "replied" && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowViewModal(false);
                    handleReply(selectedComm);
                  }}
                >
                  <FiSend style={{ marginRight: "0.5rem" }} />
                  Reply
                </button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Reply Modal */}
      <Modal
        isOpen={showReplyModal}
        onClose={() => {
          setShowReplyModal(false);
          setSelectedComm(null);
          setReplyText("");
        }}
        title="Reply to Member"
      >
        {selectedComm && (
          <div>
            <div
              style={{
                backgroundColor: "#f5f5f5",
                padding: "1rem",
                borderRadius: "8px",
                marginBottom: "1rem",
              }}
            >
              <small style={{ color: "#666" }}>
                Original Message from {selectedComm.member_name}:
              </small>
              <p style={{ margin: "0.5rem 0", fontStyle: "italic" }}>
                <strong>{selectedComm.subject}</strong>
                <br />
                {selectedComm.message}
              </p>
            </div>

            <div className="form-group">
              <label>Your Reply *</label>
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply here..."
                rows={5}
                style={{ resize: "vertical" }}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "1rem",
                marginTop: "1.5rem",
              }}
            >
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowReplyModal(false);
                  setSelectedComm(null);
                  setReplyText("");
                }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={submitReply}
                disabled={submitting}
              >
                {submitting ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default MemberMessages;
